import { dialog } from "../../nq-component";
import BaseListPresenter from "../../base/BaseListPresenter";

class MonthsListPagePresenter extends BaseListPresenter {
  constructor(
    view,
    findObjectUseCase,
    countObjectUseCase,
    deleteObjectUseCase,
    upsertUseCase,
    getCurrentUserUseCase,
  ) {
    super(view, findObjectUseCase, countObjectUseCase, deleteObjectUseCase);
    this.upsertUseCase = upsertUseCase;
    this.getCurrentUserUseCase = getCurrentUserUseCase;
  }

  componentDidMount() {
    this.init();
    this.createQuery();
    this.getObjects();
  }

  init() {
    const { year } = this.view.state;
    const user = this.view.getCurrentUser();
    const clientId = this.view.getClientId();

    const startDate = new Date(year, 0, 1);
    const endDate = new Date(year, 11, 31, 23, 59, 59, 999);
    const gte = startDate.toISOString();
    const lte = endDate.toISOString();
    this.limit = 10000;
    this.where = {
      date: {
        $gte: gte,
        $lte: lte,
      },
      $or: [{ creator: { id: user.id } }, { client: { id: user.client?.id } }],
    };
    this.search = {};
    this.filter = {};
    this.include = [""];
    this.keys = undefined; // if keys are specified, only those keys will be returned
    this.sort = { createdAt: -1 };
    this.progress = true;
    this.reset();
  }

  getMonths() {
    const year = new Date().getFullYear();

    const months = Array.from({ length: 12 }, (_, i) => {
      return new Intl.DateTimeFormat("en-US", { month: "long" }).format(
        new Date(year, i, 1),
      );
    });

    return months;
  }

  isObjectInSelectedMonths(obj, selected) {
    if (!obj?.date || !selected?.length) return false;
    const d = new Date(obj.date);
    const objMonth = d.getMonth();
    const objYear = d.getFullYear();

    return selected.some((s) => {
      const monthIndex = new Date(`${s.month} 1, ${s.year}`).getMonth();
      return (
        objMonth === monthIndex &&
        objYear === s.year &&
        obj.status === "pending"
      );
    });
  }

  async onReadyToAudit() {
    const { selected, objects } = this.view.state;
    const { client } = this.view.getCurrentUser();
    const clientId = client?.id || null;
    const collectionName = this.view.getCollectionName();
    const title = collectionName === "sales" ? "Sales" : "Purchases";

    const months = selected.map((s) => s.month);
    const selectedMonths =
      months.length <= 1
        ? (months[0] ?? "")
        : months.slice(0, -1).join(", ") + " and " + months[months.length - 1];

    if (!selected?.length) return;

    try {
      this.view.showProgress();
      const { updated, count } = await this.markReadyToAudit(selected, objects);
      this.view.setObjects(updated);
      this.view.setState({ selected: [] });
      this.view.hideProgress();
      const collectionName = this.view.getCollectionName?.();
      const route = collectionName ? `/months/${collectionName}` : undefined;

      await this.createNotification({
        title: `${title} ready to audit`,
        description: `${count} transaction(s) marked as ready to audit for ${selectedMonths}.`,
        route: route,
        client: { id: clientId },
      });

      this.view.showSuccess(
        `${count} transaction(s) marked as ready to audit.`,
      );
    } catch (error) {
      this.view.hideProgress();
      this.view.showError(error);
    }
  }

  async markReadyToAudit(selected, objects) {
    const collection = this.view.getCollectionName();
    const toUpdate = objects.filter((obj) =>
      this.isObjectInSelectedMonths(obj, selected),
    );
    if (!toUpdate.length) {
      return { updated: [], count: 0 };
    }

    const updated = [];
    for (const obj of toUpdate) {
      const updatedObject = { ...obj, status: "for_audit" };
      const result = await this.upsertUseCase.execute(
        collection,
        updatedObject,
      );
      updated.push(result ?? updatedObject);
    }

    const updatedIds = new Set(updated.map((r) => r?.id));
    const newObjects = objects.map((o) =>
      updatedIds.has(o?.id) ? (updated.find((u) => u?.id === o?.id) ?? o) : o,
    );

    return { updated: newObjects, count: updated.length };
  }
}

export default MonthsListPagePresenter;
